import type { MessageKey } from '@freightapi/i18n'
import {
  Button,
  DocumentList,
  formatDateTime,
  ShipmentView,
  type ShipmentViewSignature,
  Spinner,
} from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { IntegrityBadge } from '../components/inspect/IntegrityBadge'
import { StatusScreen } from '../components/StatusScreen'
import { ApiError } from '../lib/api'
import {
  currentPdf,
  type InspectedSignature,
  type InspectionView,
  inspectedDocumentHref,
  loadInspection,
} from '../lib/inspection'
import { useSignI18n } from '../lib/locale'

/**
 * The read-only inspection view behind the QR code on the consignment note (M1-15):
 * `SIGN_BASE_URL/i/<token>`. An authority at the roadside scans it, sees the 24 CMR boxes of the
 * current version in their own language, checks the hash chain and downloads the PDF. Nothing on
 * this page is interactive beyond that: the token grants reading, and only reading.
 */
export const Route = createFileRoute('/i/$token')({ component: InspectScreen })

/**
 * The states the ticket names, as one discriminated union. A state carries a message *key* rather
 * than translated text, so switching language re-renders the current screen instead of spending a
 * second view on the shipment's event chain.
 */
type ViewState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'rotated' }
  | { readonly kind: 'expired' }
  | {
      readonly kind: 'error'
      readonly messageKey: MessageKey<'sign'>
      readonly retryable: boolean
    }
  | { readonly kind: 'loaded'; readonly view: InspectionView }

function InspectScreen() {
  const { token } = Route.useParams()
  const { t, locale } = useSignI18n()
  const [state, setState] = useState<ViewState>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)

  // Only the `Accept-Language` of the next request, never a reason to fetch again: every view is
  // appended to the shipment's chain as `inspection.viewed`, so a language switch must not log one.
  const localeRef = useRef(locale)
  localeRef.current = locale

  // `attempt` is not read in the body; it is what "retry" increments, and re-running the effect is
  // the entire point of the dependency.
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt is the retry trigger
  useEffect(() => {
    const controller = new AbortController()
    setState({ kind: 'loading' })

    loadInspection(token, { locale: localeRef.current, signal: controller.signal })
      .then((view) => {
        if (controller.signal.aborted) return
        setState({ kind: 'loaded', view })
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setState(failureState(cause))
      })

    return () => controller.abort()
  }, [token, attempt])

  const retry = useCallback(() => setAttempt((value) => value + 1), [])

  switch (state.kind) {
    case 'loading':
      return (
        <div className="flex justify-center py-16">
          <Spinner label={t('loading')} size="lg" />
        </div>
      )

    case 'not_found':
      return (
        <StatusScreen
          tone="danger"
          title={t('inspect.not_found.title')}
          body={t('inspect.not_found.body')}
        />
      )

    case 'rotated':
      return (
        <StatusScreen
          tone="warn"
          title={t('inspect.rotated.title')}
          body={t('inspect.rotated.body')}
        />
      )

    case 'expired':
      return (
        <StatusScreen
          tone="warn"
          title={t('inspect.expired.title')}
          body={t('inspect.expired.body')}
        />
      )

    case 'error':
      return (
        <StatusScreen tone="danger" title={t('error.title')} body={t(state.messageKey)}>
          {state.retryable ? (
            <Button size="lg" onClick={retry}>
              {t('button.retry')}
            </Button>
          ) : null}
        </StatusScreen>
      )

    case 'loaded':
      return <InspectionPage token={token} view={state.view} />
  }
}

function InspectionPage({
  token,
  view,
}: {
  readonly token: string
  readonly view: InspectionView
}) {
  const { t, locale } = useSignI18n()
  const pdf = currentPdf(view)

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-semibold text-display text-ink">{t('inspect.title')}</h1>
        <p className="text-body text-muted">{t('inspect.subtitle')}</p>
        <p className="text-caption text-muted">
          {t('inspect.expires', { timestamp: formatDateTime(view.expires_at, locale) })}
        </p>
        <p className="text-caption text-muted">
          {t('inspect.viewed_at', { timestamp: formatDateTime(view.viewed_at, locale) })}
        </p>
      </header>

      <IntegrityBadge integrity={view.integrity} />

      {/* The PDF is the artefact an authority takes away, so it sits above the boxes rather than
          at the bottom of a page the length of a consignment note. */}
      <section className="flex flex-col gap-2 print:hidden">
        {pdf === undefined ? (
          <p className="text-body text-muted">{t('inspect.pdf.none')}</p>
        ) : (
          <a
            href={inspectedDocumentHref(token, pdf.id)}
            target="_blank"
            rel="noreferrer"
            data-testid="download-pdf"
            className="inline-flex min-h-touch items-center justify-center rounded-card bg-brand-600 px-4 font-medium text-body text-white hover:bg-brand-700"
          >
            {t('inspect.pdf.download')}
          </a>
        )}
        <Button variant="ghost" onClick={() => window.print()}>
          {t('inspect.print')}
        </Button>
      </section>

      <ShipmentView
        viewer="inspector"
        shipment={view.shipment}
        status={view.shipment.status}
        version={view.shipment.version}
        signatures={view.signatures.map((signature) => boxSignature(signature, view))}
        remarks={view.remarks}
        established_at={view.shipment.issued_at}
      />

      <section className="flex flex-col gap-3">
        <h2 className="font-semibold text-muted text-title">{t('inspect.documents.title')}</h2>
        <DocumentList
          documents={view.documents}
          hrefFor={(document) => inspectedDocumentHref(token, document.id)}
        />
      </section>
    </div>
  )
}

/**
 * A signature as boxes 22 to 24 draw it. The endpoint answers `verified` rather than the hash that
 * was signed, and a verified signature is by definition one whose `document_hash` equals the one
 * of the current version — so that is the fragment shown. A superseded signature bound a hash this
 * view was never given, and shows none rather than the wrong one.
 */
function boxSignature(signature: InspectedSignature, view: InspectionView): ShipmentViewSignature {
  return {
    role: signature.role,
    method: signature.method,
    trust_level: signature.trust_level,
    signer: signature.signer,
    signed_at: signature.signed_at,
    verified: signature.verified,
    ...(signature.verified ? { document_hash: view.shipment.document_hash } : {}),
  }
}

/** Maps an API failure onto the screen it deserves; anything unknown is the generic error. */
function failureState(cause: unknown): ViewState {
  if (!(cause instanceof ApiError)) {
    return { kind: 'error', messageKey: 'error.unexpected', retryable: false }
  }
  switch (cause.code) {
    case 'not_found':
      return { kind: 'not_found' }
    case 'token_rotated':
      return { kind: 'rotated' }
    case 'token_expired':
    case 'link_expired':
      return { kind: 'expired' }
    default:
      return { kind: 'error', messageKey: cause.messageKey, retryable: cause.isRetryable }
  }
}
